import React from 'react';

import facebookLogo from '~/images/facebook-logo-bunnynet.svg';
import wordpressLogo from '~/images/wordpress-logo-bunnynet.svg';
import g2Logo from '~/images/g2-logo-bunnynet.svg';
import trustpilotLogo from '~/images/trustpilot-logo-bunnynet.svg';
import reusableConfig from '~/data/config/reusable-images.json';
import { graphql } from 'gatsby';
import TrustBox, { TrustBoxType } from './TrustpilotWidget';

interface RatingProps {
  block: RatingBlock;
  vars: RatingVars;
}

interface RatingBlock {
  _template: string;
  theme: string;
  trustpilot_theme?: string;
  trustpilot_type?: string;
  settings: Settings;
  type: string;
  platform?: string[];
  css_class_name: string;
}

interface RatingVars {
  facebook_rating: string;
  wordpress_rating: string;
  g2_rating: string;
  trustpilot_rating: string;
}

const getPlatformLogo = (platform: string) => {
  switch (platform) {
    case 'facebook':
      return {
        src: facebookLogo,
        alt: 'Facebook rating - bunny.net',
        title: 'Facebook rating - bunny.net',
      };
    case 'wordpress':
      return {
        src: wordpressLogo,
        alt: 'Wordpress rating - bunny.net',
        title: 'Wordpress rating - bunny.net',
      };
    case 'g2':
      return {
        src: g2Logo,
        alt: 'g2 rating - bunny.net',
        title: 'g2 rating - bunny.net',
      };
    default:
    case 'trustpilot':
      return {
        src: trustpilotLogo,
        alt: 'Trustpilot rating - bunny.net',
        title: 'Trustpilot rating - bunny.net',
      };
  }
};

const Rating: React.FC<RatingProps> = ({ block, vars }) => {
  const facebookLogo = getPlatformLogo('facebook');
  const wordpressLogo = getPlatformLogo('wordpress');
  const g2Logo = getPlatformLogo('g2');
  const trustpilotLogo = getPlatformLogo('trustpilot');

  return (
    <section className={`block__rating block ${block.css_class_name}`}>
      <div className='container'>
        <div className='row'>
          <div className='rating-col col-lg-3 col-6'>
            <div className='rating'>
              <i className='fa fa-star'></i> <span>{vars.facebook_rating}</span>
            </div>
            <a
              href='https://www.facebook.com/bunnycdn'
              target='_blank'
              rel='noreferrer'
            >
              <img
                src={facebookLogo.src}
                alt={facebookLogo.alt}
                title={facebookLogo.title}
              />
            </a>
          </div>

          <div className='rating-col col-lg-3 col-6'>
            <div className='rating'>
              <i className='fa fa-star'></i>{' '}
              <span>{vars.wordpress_rating}</span>
            </div>
            <a
              href='https://wordpress.org/support/plugin/bunnycdn/'
              target='_blank'
              rel='noopener'
            >
              <img
                src={wordpressLogo.src}
                alt={wordpressLogo.alt}
                title={wordpressLogo.title}
              />
            </a>
          </div>

          <div className='rating-col col-lg-3 col-6'>
            <div className='rating'>
              <i className='fa fa-star'></i> <span>{vars.g2_rating}</span>
            </div>
            <a
              href='https://www.g2.com/products/bunnycdn/reviews'
              target='_blank'
              rel='noopener'
            >
              <img
                src={g2Logo.src}
                alt={g2Logo.alt}
                title={g2Logo.title}
              />
            </a>
          </div>

          <div className='rating-col col-lg-3 col-6'>
            <TrustBox type={'53aa8807dec7e10d38f59f32'} />
            {/* <div className='rating'>
              <i className='fa fa-star'></i>{' '}
              <span>{vars.trustpilot_rating}</span>
            </div>
            <a
              href='https://www.trustpilot.com/review/bunnycdn.com'
              target='_blank'
              rel='noopener'
            >
              <img
                src={trustpilotLogo.src}
                alt={trustpilotLogo.alt}
                title={trustpilotLogo.title}
              />
            </a> */}
          </div>
        </div>
      </div>
    </section>
  );
};

const getPlatformDetails = (
  platform: string,
  theme: string,
  vars: RatingVars
) => {
  const ratingKey = `${platform}_rating` as keyof RatingVars;
  // look into config for reusable images
  const platformImages = reusableConfig.company_rating.find(
    ({ variable }) => variable === ratingKey
  );
  if (!platformImages) {
    return null;
  }
  // select the image based on the theme
  const image = platformImages.images.find(
    ({ theme: imageTheme }) => imageTheme === theme
  );
  if (!image) {
    return null;
  }
  // look into site config for rating
  const rating = vars[ratingKey];
  if (rating === undefined) {
    return null;
  }
  // get the text for the platform
  const text = getLabelText(platform);
  return {
    ...image,
    rating,
    link: platformImages.link,
    text,
  };
};

const getLabelText = (platform: string) => {
  switch (platform) {
    case 'facebook':
      return 'on Facebook';
    case 'wordpress':
      return 'on Wordpress';
    case 'g2':
      return 'on G2';
    default:
    case 'trustpilot':
      return 'on Trustpilot';
  }
};

export const RatingLabeled: React.FC<{
  theme: string;
  title?: string;
  justify_content: string;
  platforms: string[];
  trustpilot_theme: string;
  trustpilot_type: string;
  vars: RatingVars;
}> = ({
  theme,
  trustpilot_theme,
  trustpilot_type,
  title,
  justify_content,
  platforms,
  vars,
}) => {
  return (
    <div className={`block__rating block__rating--labeled block`}>
      <div
        className='block-rating__ratings'
        style={{
          justifyContent: justify_content,
        }}
      >
        {platforms.map(platformKey => {
          if (platformKey === 'trustpilot') {
            return (
              <TrustBox
                theme={trustpilot_theme}
                type={trustpilot_type as TrustBoxType}
              />
            );
          }
          const platform = getPlatformDetails(platformKey, theme, vars);
          if (!platform) {
            return null;
          }
          return (
            <a
              href={platform.link}
              key={platformKey}
              className={`block-rating block-rating--${theme}`}
            >
              <div className='block-rating__logo'>
                <img
                  src={platform.src}
                  alt={platform.alt}
                  title={platform.title || platform.alt}
                />
              </div>
              <div className='block-rating__info'>
                <div className='block-rating__rating'>
                  <span>{platform.rating}</span>
                  <i className='fa fa-star' />
                </div>
                <div className='block-rating__platform'>{platform.text}</div>
              </div>
            </a>
          );
        })}
      </div>
      {title && <p className='block-rating__title'>{title}</p>}
    </div>
  );
};

export const labeledRatingFragment = graphql`
  fragment LabeledRatingFragment on Block {
    theme
    trustpilot_theme
    trustpilot_type
    platforms
    justify_content
    align_items
  }
`;

export const labeledRatingAdditionalFragment = graphql`
  fragment LabeledRatingAdditionalFragment on RatingLabeled {
    _template
    theme
    trustpilot_theme
    trustpilot_type
    platforms
    justify_content
    align_items
  }
`;

export default Rating;
